/** @jsx jsx */
import React, {
	type ComponentType,
	type ForwardRefExoticComponent,
	useState,
	Fragment,
	forwardRef,
} from 'react';
import { styled, jsx } from '@compiled/react';
import Button from '@atlaskit/button';
import { Box, Pressable, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip, { type TooltipProps } from '@atlaskit/tooltip';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { useShowHoverPopover } from '../../common/controllers/show-hover-popover/index.tsx';
import { HoverPopover } from '../../common/ui/hover-popover/index.tsx';

export type MinimumItemData = {
	key: string | number;
	isColored?: boolean;
};

export type Props<TData extends MinimumItemData> = {
	/**
	 * Tooltip shown when the user hovers over the show more button, which appears as a "+X" button
	 * when more than 1 item is present.
	 */
	showMoreTooltip: string;
	/**
	 * An array of the items to be rendered
	 */
	items: TData[];
	/**
	 * The component used to render the items. You may choose to create your own, or use one of the
	 * prebuilt components such as `LinkItem` or `TagItem`.
	 *
	 * It will receive all of the data passed to the `items` prop, except for the `key` property.
	 */
	ItemComponent: ComponentType<Omit<TData, 'key'>>;
	/**
	 * A component that wraps the content of the show more popup. Some of the prebuilt components
	 * have a corresponding wrapper component, like `LinkItemPopupContentWrapper` or
	 * `TagItemPopupContentWrapper` available.
	 *
	 * You can use Design System primitives like `Inline` and `Stack` to customise the layout of the
	 * items in the popup.
	 */
	PopupContentWrapper?:
		| ComponentType<{
				children: React.ReactNode;
				[key: string]: unknown;
		  }>
		| ComponentType<{
				[key: string]: unknown;
		  }>
		| ForwardRefExoticComponent<{
				[key: string]: unknown;
		  }>;
	/**
	 * The maximum number of items that can be displayed in Show more popup
	 * Default value is 99 items
	 * Example: if maxLimit is 99 and items contains 100 elements, 99 of them will be shown in the popup
	 */
	maxLimit?: number;
	/**
	 * Component that can be used to override the default content inside the popup
	 * When this prop is undefined, the default content will be rendered (list of items)
	 */
	OverrideContentComponent?: React.JSX.Element;
	/**
	 * The initial open state of the Show more popup
	 * Default value is false
	 */
	initialIsOpen?: boolean;
	/**
	 * Whether to enable hover popover for the show more popup
	 * Default value is false
	 */
	isHoverPopoverEnabled?: boolean;
};

type HoverTriggerProps<TData extends MinimumItemData> = {
	list: TData[];
	renderContent: () => React.ReactNode;
};

const HoverTrigger = <TData extends MinimumItemData>({
	list,
	renderContent,
}: HoverTriggerProps<TData>) => {
	const { hoverAttributes, isHoverPopoverVisible } = useShowHoverPopover();

	return (
		<Pressable
			xcss={itemsCountStyles}
			testId="list-with-popup.ui.show-more-popup.hover-trigger"
			{...hoverAttributes}
			// eslint-disable-next-line jira/i18n/text-wrapped-in-format-message
		>
			+{list.length}
			{isHoverPopoverVisible && (
				<HoverPopover isContentInteractable isOldTagItem={!list[0]?.isColored}>
					{renderContent()}
				</HoverPopover>
			)}
		</Pressable>
	);
};

const ShowMorePopup = <TData extends MinimumItemData>({
	showMoreTooltip,
	items,
	ItemComponent,
	PopupContentWrapper = Fragment,
	maxLimit = 99,
	OverrideContentComponent = undefined,
	initialIsOpen = false,
	isHoverPopoverEnabled = false,
}: Props<TData>) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isOpen, setIsOpen] = useState(initialIsOpen);

	if (items.length === 0) {
		return null;
	}

	const list = items.slice(0, maxLimit);

	const onClickTrigger = () => {
		setIsOpen(!isOpen);
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'listWithPopup',
		);
	};

	const content = () =>
		list.map(({ key, ...item }) => (
			<Fragment key={key}>
				<ItemComponent {...item} />
			</Fragment>
		));

	const contentWithWrapper = () => (
		<>
			<Box xcss={ContainerDialogStyles}>
				{OverrideContentComponent === undefined ? (
					<PopupContentWrapper>{content()}</PopupContentWrapper>
				) : (
					OverrideContentComponent
				)}
			</Box>
			<ShortcutScope />
		</>
	);

	if (isHoverPopoverEnabled) {
		return <HoverTrigger list={list} renderContent={content} />;
	}

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="bottom"
			content={contentWithWrapper}
			testId="list-with-popup.ui.show-more-popup"
			messageId="list-with-popup.ui.show-more-popup.popup"
			messageType="transactional"
			trigger={(triggerProps) => (
				<Tooltip
					key="triggerLinksDialog"
					content={showMoreTooltip}
					tag={TooltipContainer}
					{...(fg('jsc_inline_editing_field_refactor') && { position: 'top' })}
				>
					<Button
						{...triggerProps}
						isSelected={isOpen}
						onClick={onClickTrigger}
						spacing="none"
						testId="list-with-popup.ui.show-more-popup.items-count"
					>
						<ItemsCount>+{list.length}</ItemsCount>
					</Button>
				</Tooltip>
			)}
		/>
	);
};

const TooltipContainer: TooltipProps['tag'] = forwardRef((props, ref) => (
	// eslint-disable-next-line react/jsx-props-no-spreading
	<Box {...props} ref={ref} as="span" xcss={tooltipContainerStyles} />
));

const ContainerDialogStyles = xcss({
	width: '240px',
	maxHeight: '220px',
	overflow: 'auto',
	padding: 'space.100',

	border: `2px solid ${token('color.border.focused')}`,
});

const tooltipContainerStyles = xcss({
	maxWidth: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemsCount = styled.span({
	fontWeight: token('font.weight.regular'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: token('space.250'),
	paddingInline: token('space.050'),
});

const itemsCountStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle.hovered',
	borderRadius: 'border.radius',
	position: 'relative',
	height: token('space.250'),
	paddingInline: 'space.050',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

export default ShowMorePopup;
